import { useCallback, useEffect, useState } from 'react';
import { PAGINATION } from '@erpgold/shared';
import { voucherService, Voucher, VoucherFilters } from '../services/voucher.service';

interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export const useVouchers = (initialFilters: VoucherFilters = {}) => {
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [filters, setFilters] = useState<VoucherFilters>({
    page: PAGINATION.DEFAULT_PAGE,
    limit: PAGINATION.DEFAULT_LIMIT,
    ...initialFilters,
  });
  const [meta, setMeta] = useState<PaginationMeta>({
    page: PAGINATION.DEFAULT_PAGE,
    limit: PAGINATION.DEFAULT_LIMIT,
    total: 0,
    totalPages: 0,
  });

  const fetchVouchers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await voucherService.getVouchers(filters);
      setVouchers(response.data);
      setMeta(response.meta);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch vouchers'));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchVouchers();
  }, [fetchVouchers]);

  return { vouchers, loading, error, meta, filters, setFilters, refetch: fetchVouchers };
};

export const useVoucherDetail = (id: string) => {
  const [voucher, setVoucher] = useState<Voucher | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchVoucher = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await voucherService.getVoucherById(id);
      setVoucher(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch voucher'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchVoucher();
  }, [fetchVoucher]);

  return { voucher, loading, error, refetch: fetchVoucher };
};
